/* LIG — Custom Design Intake
 * Vanilla JS for blocks/_custom-design-intake.liquid. No framework, no build.
 *
 * The intake is a @theme BLOCK rendered inside Horizon's `product-information`
 * section (the right-hand `_product-details` column, where the variant picker
 * normally sits). It has NO Add-to-Cart button of its own. This script:
 *
 *   1. resolves the standard product form (the block already binds every
 *      `properties[design_*]` input to it in Liquid via the HTML `form=`
 *      attribute — `BuyButtons-ProductForm-<section.id>` — this re-affirms it)
 *   2. uploads each reference photo to the middleware EAGERLY as it's added
 *      (downscale → POST → public Blob URL), keeping the hidden
 *      `design_photo_urls` / `design_photo_count` inputs in sync
 *   3. keeps the hidden `design_product_types` input synced from the checkboxes
 *   4. on the standard Add to Cart submit, runs a synchronous validation pass
 *      and blocks the submit (showing inline errors) if the brief is incomplete
 *
 * The standard Horizon button + cart drawer + checkout do all the actual cart
 * work — this script never calls /cart/add itself.
 */
(function () {
  'use strict';

  var ALLOWED_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;
  var ALLOWED_TYPE = /^image\/(jpe?g|png|webp|heic|heif)$/i;
  var MAX_ORIGINAL_BYTES = 20 * 1024 * 1024; // 20MB per the form spec
  var DOWNSCALE_MAX_EDGE = 2000;
  var DOWNSCALE_QUALITY = 0.85;
  var RAW_FALLBACK_MAX_BYTES = 4 * 1024 * 1024; // middleware body ceiling
  var VISION_MIN = 20;
  var VISION_MAX = 2000;
  var HEADLINE_MAX = 40; // short & punchy — 4-5 words max

  function init(root) {
    if (root.dataset.cdReady === '1') return;
    root.dataset.cdReady = '1';

    var cfg = {
      middlewareUrl: (root.dataset.middlewareUrl || '').replace(/\/+$/, ''),
      maxPhotos: parseInt(root.dataset.maxPhotos, 10) || 10,
      productFormId: root.dataset.productFormId || ''
    };

    var q = function (sel) { return root.querySelector('[data-cd="' + sel + '"]'); };

    var fileInput = q('file-input');
    var drop = q('drop');
    var thumbs = q('thumbs');
    var visionEl = root.querySelector('[data-cd-prop="vision"]');
    var visionCount = q('vision-count');
    var counterWrap = root.querySelector('.cd-counter');
    var headlineSelect = q('headline-select');
    var headlineCustom = q('headline-custom');
    var headlineText = root.querySelector('[data-cd-prop="headline-text"]');
    var headlineCount = q('headline-count');
    var productTypesInput = root.querySelector('[data-cd-prop="product-types"]');
    var photoUrlsInput = root.querySelector('[data-cd-prop="photo-urls"]');
    var photoCountInput = root.querySelector('[data-cd-prop="photo-count"]');
    var statusBox = q('status');
    var statusMsg = q('status-msg');
    var bar = q('bar');
    var errorsBox = q('errors');

    var photos = []; // { id, file, previewUrl, status:'uploading'|'done'|'error', url, error }
    var nextId = 1;

    /* ---------- bind the design_* inputs to the standard product form ---------- */

    function findProductForm() {
      return (
        (cfg.productFormId && document.getElementById(cfg.productFormId)) ||
        document.querySelector('product-form-component form[action*="/cart"]') ||
        document.querySelector('form[id^="BuyButtons-ProductForm-"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    var productForm = null;

    function bindToForm() {
      productForm = findProductForm();
      if (!productForm) return false;
      var __cdFormId = productForm.getAttribute('id');
      if (!__cdFormId) { __cdFormId = 'cd-product-form'; productForm.setAttribute('id', __cdFormId); }
      root.querySelectorAll('[data-cd-prop]').forEach(function (el) {
        el.setAttribute('form', __cdFormId);
      });
      productForm.addEventListener('submit', onProductFormSubmit, true);
      return true;
    }

    // The product section is on the same page; with `defer` the DOM is parsed,
    // but retry a few times in case of late hydration.
    if (!bindToForm()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (bindToForm() || tries > 20) clearInterval(iv);
      }, 150);
    }

    /* ---------- photo handling (eager upload) ---------- */

    function fileAllowed(file) {
      return ALLOWED_TYPE.test(file.type || '') || ALLOWED_EXT.test(file.name || '');
    }

    function addFiles(fileList) {
      var rejected = [];
      Array.prototype.forEach.call(fileList, function (file) {
        if (photos.length >= cfg.maxPhotos) {
          rejected.push(file.name + ' — limit is ' + cfg.maxPhotos + ' photos');
          return;
        }
        if (!fileAllowed(file)) {
          rejected.push(file.name + ' — must be JPG, PNG, HEIC or WEBP');
          return;
        }
        if (file.size > MAX_ORIGINAL_BYTES) {
          rejected.push(file.name + ' — over 20MB');
          return;
        }
        var p = {
          id: nextId++,
          file: file,
          previewUrl: URL.createObjectURL(file),
          status: 'uploading',
          url: null,
          error: null
        };
        photos.push(p);
        processPhoto(p); // eager upload
      });
      if (rejected.length) showErrors(rejected);
      else clearErrors();
      renderThumbs();
      syncPhotoInputs();
    }

    function processPhoto(p) {
      downscale(p.file)
        .then(function (prepared) {
          if (!prepared) {
            throw new Error('could not be processed in the browser — re-save as JPG/PNG');
          }
          return uploadOne(prepared, p.file.name || ('photo-' + p.id));
        })
        .then(function (url) {
          p.status = 'done';
          p.url = url;
        })
        .catch(function (err) {
          p.status = 'error';
          p.error = (err && err.message) ? err.message : 'upload failed';
        })
        .then(function () {
          renderThumbs();
          syncPhotoInputs();
        });
    }

    function removePhoto(id) {
      var idx = -1;
      for (var i = 0; i < photos.length; i++) { if (photos[i].id === id) { idx = i; break; } }
      if (idx < 0) return;
      try { URL.revokeObjectURL(photos[idx].previewUrl); } catch (e) {}
      photos.splice(idx, 1);
      renderThumbs();
      syncPhotoInputs();
    }

    function renderThumbs() {
      thumbs.innerHTML = '';
      photos.forEach(function (p) {
        var cell = document.createElement('div');
        cell.className = 'cd-thumb';
        if (p.status === 'done') cell.className += ' is-done';
        if (p.status === 'error') cell.className += ' is-error';

        var img = document.createElement('img');
        img.src = p.previewUrl;
        img.alt = 'Reference photo';
        cell.appendChild(img);

        var del = document.createElement('button');
        del.type = 'button';
        del.className = 'cd-thumb__del';
        del.setAttribute('aria-label', 'Remove photo');
        del.innerHTML = '&times;';
        del.addEventListener('click', function () { removePhoto(p.id); });
        cell.appendChild(del);

        var state = document.createElement('span');
        state.className = 'cd-thumb__state';
        state.textContent =
          p.status === 'uploading' ? 'uploading…' :
          p.status === 'done' ? 'uploaded' :
          p.status === 'error' ? (p.error || 'failed') : '';
        cell.appendChild(state);

        thumbs.appendChild(cell);
      });
    }

    function syncPhotoInputs() {
      var doneUrls = photos.filter(function (p) { return p.status === 'done' && p.url; })
        .map(function (p) { return p.url; });
      if (photoUrlsInput) photoUrlsInput.value = doneUrls.join('\n');
      if (photoCountInput) photoCountInput.value = String(doneUrls.length);

      var uploading = photos.some(function (p) { return p.status === 'uploading'; });
      var errored = photos.filter(function (p) { return p.status === 'error'; }).length;
      if (uploading) {
        setStatus('Uploading your photos… (' + doneUrls.length + '/' + photos.length + ')',
          photos.length ? (doneUrls.length / photos.length) * 100 : 0);
      } else if (errored > 0) {
        setStatus(errored + ' photo(s) failed to upload — remove and re-add them.', 100, true);
      } else if (photos.length > 0) {
        setStatus('All ' + doneUrls.length + ' photo(s) uploaded.', 100);
      } else {
        statusBox.hidden = true;
      }
    }

    /* ---------- client-side downscale + upload ---------- */

    function downscale(file) {
      return new Promise(function (resolve) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          URL.revokeObjectURL(url);
          try {
            var w = img.naturalWidth || img.width;
            var h = img.naturalHeight || img.height;
            var scale = Math.min(1, DOWNSCALE_MAX_EDGE / Math.max(w, h));
            var cw = Math.max(1, Math.round(w * scale));
            var ch = Math.max(1, Math.round(h * scale));
            var canvas = document.createElement('canvas');
            canvas.width = cw;
            canvas.height = ch;
            canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
            canvas.toBlob(function (blob) {
              resolve(blob ? { blob: blob, contentType: 'image/jpeg', ext: 'jpg' } : rawFallback(file));
            }, 'image/jpeg', DOWNSCALE_QUALITY);
          } catch (e) {
            resolve(rawFallback(file));
          }
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          resolve(rawFallback(file)); // e.g. HEIC on a non-Safari browser
        };
        img.src = url;
      });
    }

    function rawFallback(file) {
      if (file.size > RAW_FALLBACK_MAX_BYTES) return null;
      var ct = file.type && ALLOWED_TYPE.test(file.type) ? file.type : 'image/jpeg';
      var ext = (file.name.match(ALLOWED_EXT) || ['', 'jpg'])[1] || 'jpg';
      return { blob: file, contentType: ct, ext: ext.toLowerCase() };
    }

    function uploadOne(prepared, baseName) {
      if (!cfg.middlewareUrl) {
        return Promise.reject(new Error('form not configured (missing middleware URL)'));
      }
      var fname = baseName.replace(/\.[^.]+$/, '') + '.' + prepared.ext;
      var endpoint = cfg.middlewareUrl + '/api/custom-design/upload?filename=' +
        encodeURIComponent(fname);
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': prepared.contentType },
        body: prepared.blob
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || !data.url) {
            throw new Error(data.error || ('upload failed (' + res.status + ')'));
          }
          return data.url;
        });
      });
    }

    /* ---------- field sync ---------- */

    function selectedProductTypes() {
      return Array.prototype.slice
        .call(root.querySelectorAll('.cd-checks input[type="checkbox"]:checked'))
        .map(function (el) { return el.value; });
    }

    function syncProductTypes() {
      if (productTypesInput) productTypesInput.value = selectedProductTypes().join(', ');
    }

    function headlineIsCustom() {
      return /custom/i.test(headlineSelect ? headlineSelect.value : '');
    }

    function syncHeadlineCount() {
      if (!headlineText) return;
      // Hard cap at HEADLINE_MAX even if the maxlength attribute is bypassed.
      if ((headlineText.value || '').length > HEADLINE_MAX) {
        headlineText.value = headlineText.value.slice(0, HEADLINE_MAX);
      }
      if (headlineCount) headlineCount.textContent = String((headlineText.value || '').length);
    }

    function syncHeadline() {
      var custom = headlineIsCustom();
      if (headlineCustom) headlineCustom.hidden = !custom;
      if (headlineText) {
        // Disabled inputs don't submit — so no empty property unless "custom".
        headlineText.disabled = !custom;
        if (!custom) headlineText.value = '';
      }
      syncHeadlineCount();
    }

    /* ---------- validation (synchronous — runs on the product form submit) ---------- */

    function validate() {
      var errs = [];
      if (selectedProductTypes().length === 0) {
        errs.push('Pick at least one product type.');
      }
      var vision = (visionEl && visionEl.value || '').trim();
      if (vision.length < VISION_MIN) {
        errs.push('Describe your vision in at least ' + VISION_MIN + ' characters.');
      } else if (vision.length > VISION_MAX) {
        errs.push('Your vision is over the ' + VISION_MAX + '-character limit.');
      }
      var done = photos.filter(function (p) { return p.status === 'done'; }).length;
      var uploading = photos.some(function (p) { return p.status === 'uploading'; });
      var errored = photos.some(function (p) { return p.status === 'error'; });
      if (photos.length === 0) {
        errs.push('Add at least one reference photo.');
      } else if (uploading) {
        errs.push('Your photos are still uploading — give it a moment, then try again.');
      } else if (errored) {
        errs.push('A photo failed to upload — remove it and re-add it.');
      } else if (done === 0) {
        errs.push('No photos finished uploading — re-add them.');
      }
      if (headlineIsCustom()) {
        var hl = (headlineText && headlineText.value || '').trim();
        if (!hl) {
          errs.push('Enter your custom headline text — or keep the standard Life Is Golden branding.');
        } else if (hl.length > HEADLINE_MAX) {
          errs.push('Keep your custom headline under ' + HEADLINE_MAX + ' characters — 4–5 words max.');
        }
      }
      return errs;
    }

    function onProductFormSubmit(e) {
      var errs = validate();
      if (errs.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showErrors(errs);
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // valid — make sure the hidden inputs are current, then let Horizon proceed
      syncProductTypes();
      syncPhotoInputs();
      clearErrors();
    }

    /* ---------- UI helpers ---------- */

    function showErrors(list) {
      if (!list || !list.length) { clearErrors(); return; }
      var html = '<strong>Please finish your design brief:</strong><ul>';
      list.forEach(function (msg) {
        html += '<li>' + String(msg).replace(/[<>&]/g, '') + '</li>';
      });
      html += '</ul>';
      errorsBox.innerHTML = html;
      errorsBox.hidden = false;
    }
    function clearErrors() {
      errorsBox.hidden = true;
      errorsBox.innerHTML = '';
    }
    function setStatus(msg, pct, isError) {
      statusBox.hidden = false;
      statusMsg.textContent = msg;
      statusMsg.style.color = isError ? '#a8362b' : '';
      if (typeof pct === 'number') bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }

    /* ---------- wiring ---------- */

    drop.addEventListener('click', function () { fileInput.click(); });
    drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });
    fileInput.addEventListener('change', function () {
      if (fileInput.files && fileInput.files.length) addFiles(fileInput.files);
      fileInput.value = '';
    });
    ['dragenter', 'dragover'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) {
        e.preventDefault(); e.stopPropagation();
        drop.classList.add('is-hover');
      });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) {
        e.preventDefault(); e.stopPropagation();
        drop.classList.remove('is-hover');
      });
    });
    drop.addEventListener('drop', function (e) {
      var dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length) addFiles(dt.files);
    });

    root.querySelectorAll('.cd-checks input[type="checkbox"]').forEach(function (cb) {
      cb.addEventListener('change', function () { syncProductTypes(); clearErrors(); });
    });

    if (visionEl && visionCount) {
      var updateCount = function () {
        var len = (visionEl.value || '').length;
        visionCount.textContent = String(len);
        if (counterWrap) counterWrap.classList.toggle('is-short', len > 0 && len < VISION_MIN);
      };
      visionEl.addEventListener('input', updateCount);
      updateCount();
    }

    if (headlineSelect) {
      headlineSelect.addEventListener('change', syncHeadline);
    }
    if (headlineText) {
      headlineText.addEventListener('input', syncHeadlineCount);
    }

    // initial sync
    syncProductTypes();
    syncHeadline();
    syncPhotoInputs();
  }

  function killExpress() {
    // Custom design requires a complete brief, so neutralize express / dynamic
    // checkout (Shop Pay, PayPal, etc.) — otherwise it bypasses the form's
    // validation. This script only loads on the custom-design product page, so
    // this is scoped to that page. MutationObserver keeps them hidden if the
    // Shop Pay SDK re-injects after load.
    var SEL = '.shopify-payment-button, shopify-accelerated-checkout, .accelerated-checkout-block, .additional-checkout-buttons';
    function hide() { document.querySelectorAll(SEL).forEach(function (el) { el.style.setProperty('display', 'none', 'important'); }); }
    hide();
    try { new MutationObserver(hide).observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
  }

  function boot() {
    if (!document.querySelector('.cd-intake[data-cd-root]')) return;
    killExpress();
    document.querySelectorAll('.cd-intake[data-cd-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the theme editor re-renders the host section or this block.
  function reinitFrom(target) {
    if (!target || !target.querySelectorAll) return;
    target.querySelectorAll('.cd-intake[data-cd-root]').forEach(init);
  }
  document.addEventListener('shopify:section:load', function (e) { reinitFrom(e.target); });
  document.addEventListener('shopify:block:select', function (e) { reinitFrom(e.target); });
})();
